/**
 * The offline outbox (TICKET FE-016).
 *
 * A durable, ordered queue of operations that could not reach the server. The
 * promise it keeps is the one the Frontend Spec makes in §25: *ne jamais perdre
 * une saisie à cause d'une perte réseau*.
 *
 * IndexedDB rather than localStorage: the queue has to survive a reload, hold a
 * photo taken in a basement, and grow past a few megabytes. localStorage is
 * synchronous, capped, and cannot store a Blob.
 *
 * See docs/adr/0009-offline-outbox-and-service-worker.md for why only requests
 * carrying an idempotency key are ever queued, and why order is a business rule
 * rather than an implementation detail.
 */

const DB_NAME = "hdv-offline";
const DB_VERSION = 1;
const STORE = "outbox";

export type OutboxStatus = "PENDING" | "CONFLICT" | "FAILED";

export interface OutboxEntry {
  /** Monotonic: the queue drains in this order, and the order matters. */
  id: number;
  createdAt: number;
  /** What the operator did, in their words. Shown in the queue panel. */
  label: string;
  method: "POST";
  path: string;
  /** JSON body, or the fields of a multipart upload. */
  body?: unknown;
  /** A photo taken offline. Uploaded as multipart when the queue drains. */
  file?: { blob: Blob; name: string; field: string };
  /** The key that makes replaying this safe. Never absent — see the ADR. */
  idempotencyKey: string;
  status: OutboxStatus;
  attempts: number;
  /** Filled when the server refused it. Kept so a human can read it. */
  error?: { code: string; message: string; status: number };
  /** Query keys to invalidate once this lands. */
  invalidates?: string[];
}

export type NewEntry = Omit<OutboxEntry, "id" | "createdAt" | "status" | "attempts">;

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB indisponible"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB refusé"));
  });
  return dbPromise;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = work(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("IndexedDB refusé"));
      }),
  );
}

/**
 * Whether the queue can be used at all.
 *
 * Private browsing and hardened profiles block IndexedDB. When they do, the
 * application still works — it simply reports network failures the way it did
 * before, rather than pretending a saisie was kept.
 */
export async function isAvailable(): Promise<boolean> {
  try {
    await open();
    return true;
  } catch {
    return false;
  }
}

export async function enqueue(entry: NewEntry): Promise<OutboxEntry> {
  const record = {
    ...entry,
    createdAt: Date.now(),
    status: "PENDING" as const,
    attempts: 0,
  };
  const id = (await run<IDBValidKey>("readwrite", (store) => store.add(record))) as number;
  return { ...record, id };
}

export async function list(): Promise<OutboxEntry[]> {
  try {
    const all = await run<OutboxEntry[]>("readonly", (store) => store.getAll());
    return all.sort((a, b) => a.id - b.id);
  } catch {
    return [];
  }
}

/** The next thing to send. Never skips ahead: order is the point. */
export async function head(): Promise<OutboxEntry | null> {
  const all = await list();
  return all.find((entry) => entry.status === "PENDING") ?? null;
}

export async function update(entry: OutboxEntry): Promise<void> {
  await run("readwrite", (store) => store.put(entry));
}

export async function remove(id: number): Promise<void> {
  await run("readwrite", (store) => store.delete(id));
}

export async function clear(): Promise<void> {
  await run("readwrite", (store) => store.clear());
}

/**
 * Put a refused entry back in line.
 *
 * The error is cleared: keeping it would make the queue show a failure that no
 * longer applies, and a stale error is read as a current one.
 */
export async function retry(id: number): Promise<void> {
  const all = await list();
  const entry = all.find((item) => item.id === id);
  if (!entry) return;
  await update({ ...entry, status: "PENDING", error: undefined });
}

export function isBlocked(entries: OutboxEntry[]): boolean {
  return entries.some((entry) => entry.status !== "PENDING");
}

export function counts(entries: OutboxEntry[]): {
  pending: number;
  conflict: number;
  failed: number;
} {
  return {
    pending: entries.filter((entry) => entry.status === "PENDING").length,
    conflict: entries.filter((entry) => entry.status === "CONFLICT").length,
    failed: entries.filter((entry) => entry.status === "FAILED").length,
  };
}
